import { Controller } from "stimulus";
import { DateTime } from "luxon";
import cart from "util/cart.js";
import newCart from "util/new_cart.js";
import { _moneyFormat } from "util/formatters";
import { useDispatch } from "stimulus-use";

export default class extends Controller {

  static targets = [
    "unit",
    "unitIdInput",
    "unitTypeInput",
    "datesLabel",
    "arriveInput",
    "departInput",
    "adultsInput",
    "childrenInput",
    "totalInput",
    "feeItems",
    "breakdownButton",
    "feesTotal",
    "nightlyRate",
    "totalRate",
    "totalPrice",
    "pricePerGuest",
    "couponSavingsBanner",
    "couponSavingsCode",
    "couponSavingsValue"
  ];

  currentQuote = undefined;

  connect() {
    useDispatch(this);
    this.updateDatesLabel();

    this.cart_item_id = parseInt(this.element.dataset.cartItemId)

    newCart.get_item_quote(this.cart_item_id)
        .then(quote => {
          this.handleItemQuoteResponse(quote);
        })
        .catch(error => console.log(error))
  }

  disconnect() {
    window.dispatchEvent(new CustomEvent("cartUpdated"));
  }

  toggleBreakdown() {
    this.feeItemsTarget.classList.toggle('open')
    this.breakdownButtonTarget.classList.toggle('open')
  }

  updateDatesLabel() {
    if (!this.arriveInputTarget.value || !this.departInputTarget.value) return;

    // replace '-' with '/' to avoid effecting timezone when creating date object from a string
    const arriveDate = DateTime.fromJSDate(new Date(this.arriveInputTarget.value.replace(/-/g, "/")));
    const departDate = DateTime.fromJSDate(new Date(this.departInputTarget.value.replace(/-/g, "/")));
    const arriveDateFormatted = arriveDate.toFormat('d MMM');
    const departDateFormatted = departDate.toFormat('d MMM, yyyy');
    this.datesLabelTarget.textContent = `${arriveDateFormatted} - ${departDateFormatted}`;
  }

  updateGuests(event) {
    const { detail: { adults, children } } = event;

    this.adultsInputTarget.value = adults;
    this.childrenInputTarget.value = children;
    this.updatePricePerGuest();
    cart.update(this.unitTypeInputTarget.value, this.unitIdInputTarget.value, this.cartItemParams());
  }

  cartItemParams() {
    return {
      unit_id: this.unitIdInputTarget.value,
      type: this.unitTypeInputTarget.value,
      arrival_date: this.arriveInputTarget.value,
      departure_date: this.departInputTarget.value,
      adults: this.adultsInputTarget.value,
      children: this.childrenInputTarget.value
    };
  }

  handleItemQuoteResponse(response) {
    if (response.status === "error") {
      this.element.classList.add("disabled");
    } else {
      this.currentQuote = response;
      this.setPaymentDetails(response);
    }

    this._updateDataLayer(response);
    this.dispatch("quoteUpdated");
  }

  setPaymentDetails(quote) {
    const {
      guest_fees: feeItems,
      nightly_rate: nightlyRate,
      num_nights: numNights,
      total_rate: totalRate,
      total_taxes: taxes,
      payment_plan: paymentPlan,
      total
    } = quote;
    const totalWithoutTaxes = total - taxes;

    // When a promo applies, the quote returns the pre-discount nightly/room
    // rate plus total_discount (the actual amount deducted from rent). Show the
    // discounted rate so the cart matches the PDP and what the guest pays.
    const totalDiscount = parseFloat(quote.total_discount) || 0;
    const promoApplied = quote.promo_code && totalDiscount > 0;
    const displayedTotalRate = totalRate - totalDiscount;
    const displayedNightly = numNights > 0 ? displayedTotalRate / numNights : nightlyRate;

    this.setFees(feeItems);
    this.setNightlyAndTotalRate(displayedNightly, numNights, displayedTotalRate);
    this.setTotalPrice(totalWithoutTaxes);
    this.updatePricePerGuest();
    this.updateCouponSavings(quote);
  }

  updateCouponSavings(quote) {
    if (!this.hasCouponSavingsBannerTarget) return;

    const code = quote.promo_code;
    const value = parseFloat(quote.total_discount);

    if (code && value > 0) {
      if (this.hasCouponSavingsCodeTarget) this.couponSavingsCodeTarget.textContent = code;
      if (this.hasCouponSavingsValueTarget) this.couponSavingsValueTarget.textContent = _moneyFormat(value);
      this.couponSavingsBannerTarget.style.display = "";
    } else {
      this.couponSavingsBannerTarget.style.display = "none";
    }
  }

  setFees(feeItems) {
    this.feeItemsTarget.innerHTML = "";
    let feesSum = 0;
    feeItems.forEach(item => {
      const feePrice = parseFloat(item.value);
      const feeItemHtml = `
        <div class="payment-details-card-row">
          <div class="item-name">${item.name}</div>
          <div class="item-value">${_moneyFormat(feePrice)}</div>
        </div>
      `;
      feesSum += parseFloat(item.value);
      this.feeItemsTarget.insertAdjacentHTML('beforeend', feeItemHtml);
    });
    this.feesTotalTarget.textContent = _moneyFormat(feesSum);
  }

  setNightlyAndTotalRate(nightlyRate, numNights, totalRate) {
    this.nightlyRateTarget.textContent = `
      $${parseFloat(nightlyRate).toLocaleString('en-US')} x ${numNights} nights
    `;
    this.totalRateTarget.textContent = _moneyFormat(totalRate);
  }

  setTotalPrice(total) {
    const totalPrice = parseFloat(total);
    this.totalInputTarget.value = totalPrice;
    this.totalPriceTarget.textContent = _moneyFormat(totalPrice);
  }

  updatePricePerGuest() {
    const guestsCount = parseInt(this.adultsInputTarget.value, 10) + parseInt(this.childrenInputTarget.value, 10);
    const totalPrice = parseFloat(this.totalInputTarget.value);
    let text;

    if (!totalPrice || (guestsCount === 0)) {
      text = "$--";
    } else {
      const pricePerGuestPerNight = totalPrice / guestsCount;
      text = _moneyFormat(pricePerGuestPerNight);
    }
    this.pricePerGuestTarget.textContent = text;
  }

  removeFromCart() {
    newCart.remove_item(this.cart_item_id);
    this._removeDataLayer();
    this.element.remove();
    window.dispatchEvent(new CustomEvent("cartUpdated"));
  }

  removeUnavailableUnit(event) {
    this.unitTargets.forEach((unit) => {
      const { unitId } = unit.dataset;
      const { unitType } = unit.dataset;


      if (unitId === event.detail.unit_id && unitType === event.detail.unit_type) {
        unit.remove();
      }
    })
  }

  // nightly_rate is the pre-discount room rate. Report the discounted nightly
  // rate (gross room rate minus the actual discount, per night) so analytics
  // matches what the guest actually pays.
  _discountedNightly(quote) {
    const numNights = quote.num_nights;
    const totalDiscount = parseFloat(quote.total_discount) || 0;
    const discountedRoomRate = (parseFloat(quote.total_rate) || 0) - totalDiscount;
    return numNights > 0
      ? Number((discountedRoomRate / numNights).toFixed(2))
      : quote.nightly_rate;
  }

  // Discounted room total (gross room rate minus the actual discount). Reported as
  // the item price so each booking is one line of quantity 1.
  _discountedRoomTotal(quote) {
    const totalDiscount = parseFloat(quote.total_discount) || 0;
    return Number(((parseFloat(quote.total_rate) || 0) - totalDiscount).toFixed(2));
  }

  _updateDataLayer(quote) {
    const code = this.element.dataset.unitCode
    {
      const item = window.cartLayer.ecommerce.items.find(d => d.item_id === code);
      if (item) {
        item.price = this._discountedRoomTotal(quote);
        item.quantity = 1;
        item.price_per_night = this._discountedNightly(quote);
        item.number_of_nights = quote.num_nights;
        quote.guest_fees.forEach(fee => {
          window.cartLayer.ecommerce.items.push({
            item_id: `${code}-${fee.name}`,
            item_name: fee.name,
            item_brand: 'Villatel',
            item_category: 'Fee',
            price: fee.unit_value,
            quantity: fee.quantity
          })
        })
      }
    }
  }

  _removeDataLayer() {
    const code = this.element.dataset.unitCode
    const quote = this.currentQuote;

    const item = window.cartLayer.ecommerce.items.find(d => d.item_id === code);

    if (item) {
      const newItem = {...item}
      newItem.price = this._discountedRoomTotal(quote);
      newItem.quantity = 1;
      newItem.price_per_night = this._discountedNightly(quote);
      newItem.number_of_nights = quote.num_nights;

      const data = {...window.cartLayer };
      data.event_name = "remove_from_cart"
      data.ecommerce.items = [newItem];

      this.currentQuote.guest_fees.forEach(fee => {
        data.ecommerce.items.push({
          item_id: `${code}-${fee.name}`,
          item_name: fee.name,
          item_brand: 'Villatel',
          item_category: 'Fee',
          price: fee.unit_value,
          quantity: fee.quantity
        })
      })

      window.dataLayer.push(data)
    }
  }
}
